/**
 * Procedural surface textures for the biomes.
 *
 * Painted into a canvas at runtime rather than loaded: the project ships no image
 * assets, and a biome needs only a tiling pattern and a palette, which is a few
 * hundred rectangles of arithmetic. A 256px tile costs about a millisecond to
 * draw and is generated once per zone build.
 *
 * The **layout maths is pure and tested**; only `paint` touches a canvas. That is
 * the same split `models/poses.ts` has against Babylon, and for the same reason:
 * the thing that can be wrong is where the bricks go, not the call that fills a
 * rectangle.
 */

/** How a surface is patterned. */
export type PatternKind =
  /** Rectangular blocks with mortar gaps — masonry, brick, cut stone. */
  | 'masonry'
  /** Horizontal bands — sediment, sand, strata. */
  | 'strata'
  /** Angular shards — cracked basalt, shattered rock. */
  | 'shards'
  /** Nothing but noise — the abyss has no structure to read. */
  | 'noise'
  /** Cobbles: small, near-square, irregular. Town streets. */
  | 'cobbles'
  /**
   * Overlapping blotches of no particular shape — sodden ground, moss, rot.
   *
   * The marsh and the thicket floor. Distinct from `noise` in that the blotches are
   * several cells across and vary in size, so the surface reads as *wet* rather
   * than as static; distinct from `shards` in having no straight edges to align.
   */
  | 'mottled'
  /**
   * Thin seams crossing a darker ground — crystal veins, tangled roots.
   *
   * The caverns and the thicket walls. One horizontal and one vertical seam per
   * cell, each offset within it, so the result is irregular without being a grid.
   */
  | 'veins';

export interface Pattern {
  kind: PatternKind;
  /** Nominal cell size in texture pixels. */
  cell: number;
  /** Gap between cells, in pixels. */
  mortar: number;
  /** How much each cell's shade varies, 0..1. */
  jitter: number;
  /** How strongly the whole surface is grained, 0..1. */
  grain: number;
}

/** One patch to fill: position, size, and how much to lighten or darken it. */
export interface Patch {
  x: number;
  y: number;
  w: number;
  h: number;
  /** -1 darkest, 0 the base colour, +1 lightest. */
  shade: number;
}

/** The texture edge length in pixels. A power of two, for mipmaps. */
export const TEXTURE_SIZE = 256;

/**
 * A deterministic value in [0,1) from three integers.
 *
 * Hash rather than `Math.random()`: the same zone must look the same on a rebuild,
 * and two clients in the same zone must see the same walls. This is the texture
 * equivalent of the server seeding its layout.
 */
export function hash(x: number, y: number, salt: number): number {
  let h = (x * 374761393 + y * 668265263 + salt * 2147483647) | 0;
  h = (h ^ (h >>> 13)) * 1274126177;
  h = h ^ (h >>> 16);
  return ((h >>> 0) % 100000) / 100000;
}

/**
 * The patches that make up one tile of a pattern.
 *
 * Every patch is inside `0..size` on both axes, and every pattern **tiles
 * seamlessly**: a course of masonry that ran off the right edge without coming
 * back on the left would show a visible seam on every wall in the game, so
 * offset courses wrap rather than clip.
 */
export function patches(pattern: Pattern, size = TEXTURE_SIZE, salt = 0): Patch[] {
  switch (pattern.kind) {
    case 'masonry':
      return masonry(pattern, size, salt);
    case 'cobbles':
      return cobbles(pattern, size, salt);
    case 'strata':
      return strata(pattern, size, salt);
    case 'shards':
      return shards(pattern, size, salt);
    case 'mottled':
      return mottled(pattern, size, salt);
    case 'veins':
      return veins(pattern, size, salt);
    default:
      return noise(pattern, size, salt);
  }
}

/**
 * A shade offset in `[-jitter, +jitter]`.
 *
 * Returns exactly `0` when there is no jitter. Computing it as
 * `(hash - 0.5) * 2 * 0` yields **negative zero**, which is not `0` under
 * `Object.is` — so an untextured surface compared unequal to a flat one in tests,
 * and would have been a real inequality anywhere else that check is made.
 */
function jitterAt(p: Pattern, x: number, y: number, salt: number): number {
  if (p.jitter === 0) return 0;
  return (hash(x, y, salt) - 0.5) * 2 * p.jitter;
}

/** Running bond: each course offset half a block from the one below. */
function masonry(p: Pattern, size: number, salt: number): Patch[] {
  const out: Patch[] = [];
  const h = p.cell;
  const w = p.cell * 2;
  const rows = Math.max(1, Math.round(size / h));

  for (let row = 0; row < rows; row++) {
    const y = (row * size) / rows;
    // Alternate courses shift by half a block. The shift is modulo the block
    // width so the pattern still repeats across the tile edge.
    const offset = row % 2 === 0 ? 0 : w / 2;
    for (let x = -w; x < size + w; x += w) {
      const left = x + offset;
      const clipped = clip(left, y, w - p.mortar, size / rows - p.mortar, size);
      if (clipped) {
        out.push({ ...clipped, shade: jitterAt(p, row, Math.round(left), salt) });
      }
    }
  }
  return out;
}

/** Small, near-square, jittered — a street rather than a wall. */
function cobbles(p: Pattern, size: number, salt: number): Patch[] {
  const out: Patch[] = [];
  const n = Math.max(1, Math.round(size / p.cell));
  const step = size / n;

  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      const wobble = (hash(col, row, salt) - 0.5) * step * 0.25;
      const side = step - p.mortar + wobble;
      const clipped = clip(col * step, row * step, Math.max(1, side), Math.max(1, side), size);
      if (clipped) {
        out.push({ ...clipped, shade: jitterAt(p, col, row, salt + 7) });
      }
    }
  }
  return out;
}

/** Horizontal bands of varying thickness. */
function strata(p: Pattern, size: number, salt: number): Patch[] {
  const out: Patch[] = [];
  let y = 0;
  let band = 0;
  while (y < size) {
    const thickness = p.cell * (0.5 + hash(0, band, salt));
    const h = Math.min(thickness, size - y);
    out.push({ x: 0, y, w: size, h, shade: jitterAt(p, 1, band, salt + 3) });
    y += h;
    band++;
  }
  return out;
}

/** Irregular quads, as though the surface had shattered. */
function shards(p: Pattern, size: number, salt: number): Patch[] {
  const out: Patch[] = [];
  const n = Math.max(1, Math.round(size / p.cell));
  const step = size / n;

  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      const a = hash(col, row, salt);
      const b = hash(col, row, salt + 11);
      const w = step * (0.45 + a * 0.75);
      const h = step * (0.45 + b * 0.75);
      const clipped = clip(col * step + a * p.mortar, row * step + b * p.mortar, w, h, size);
      if (clipped) {
        out.push({ ...clipped, shade: jitterAt(p, col, row, salt + 19) });
      }
    }
  }
  return out;
}

/**
 * Blotches of two sizes, offset within their cells. Wet ground.
 *
 * Two passes over the same grid at different scales rather than one pass with more
 * jitter: a single scale reads as a checker however much the shades vary, and it is
 * the *overlap* of a coarse blotch and a fine one that makes a surface look sodden.
 * Every rectangle is clipped, so nothing is emitted outside the tile.
 */
function mottled(p: Pattern, size: number, salt: number): Patch[] {
  const out: Patch[] = [];

  for (const [scale, spread, seed] of [
    [1, 1.6, salt],
    [0.5, 1.1, salt + 37],
  ] as const) {
    const step = Math.max(2, p.cell * scale);
    const n = Math.max(1, Math.round(size / step));
    const cell = size / n;

    for (let row = 0; row < n; row++) {
      for (let col = 0; col < n; col++) {
        const a = hash(col, row, seed);
        const b = hash(col, row, seed + 5);
        const side = cell * (0.35 + a * spread * 0.5);
        const clipped = clip(
          col * cell + (a - 0.5) * cell * 0.6,
          row * cell + (b - 0.5) * cell * 0.6,
          side,
          side * (0.6 + b * 0.8),
          size,
        );
        if (clipped) {
          out.push({ ...clipped, shade: jitterAt(p, col, row, seed + 13) });
        }
      }
    }
  }
  return out;
}

/**
 * One horizontal and one vertical seam per cell, each offset inside it.
 *
 * The seams are `mortar` pixels thick rather than `cell` wide, so this is the one
 * pattern where `mortar` is the *drawn* thing rather than the gap between drawn
 * things — a vein is a bright line on dark rock. A cell whose seam would be
 * hairline is skipped by `clip` rather than emitted with no area.
 */
function veins(p: Pattern, size: number, salt: number): Patch[] {
  const out: Patch[] = [];
  const n = Math.max(1, Math.round(size / p.cell));
  const cell = size / n;
  const thickness = Math.max(1, p.mortar);

  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      const a = hash(col, row, salt);
      const b = hash(col, row, salt + 23);

      const across = clip(col * cell, row * cell + a * (cell - thickness), cell, thickness, size);
      if (across) {
        out.push({ ...across, shade: 0.35 + jitterAt(p, col, row, salt + 41) });
      }

      const down = clip(col * cell + b * (cell - thickness), row * cell, thickness, cell, size);
      if (down) {
        out.push({ ...down, shade: 0.35 + jitterAt(p, col, row, salt + 59) });
      }
    }
  }
  return out;
}

/** A field of single-pixel-ish specks. Structureless on purpose. */
function noise(p: Pattern, size: number, salt: number): Patch[] {
  const out: Patch[] = [];
  const step = Math.max(2, Math.round(p.cell / 3));
  for (let y = 0; y < size; y += step) {
    for (let x = 0; x < size; x += step) {
      // Clipped like every other pattern: a step that does not divide the texture
      // evenly would otherwise paint past the right and bottom edges.
      const clipped = clip(x, y, step, step, size);
      if (clipped) out.push({ ...clipped, shade: jitterAt(p, x, y, salt) });
    }
  }
  return out;
}

/**
 * Trims a patch to the texture bounds, or drops it entirely.
 *
 * Returns null rather than a zero-size patch: a rectangle of no area is a draw
 * call that does nothing, and there are hundreds of them along the edges.
 */
function clip(x: number, y: number, w: number, h: number, size: number): Omit<Patch, 'shade'> | null {
  const left = Math.max(0, x);
  const top = Math.max(0, y);
  const right = Math.min(size, x + w);
  const bottom = Math.min(size, y + h);
  if (right <= left || bottom <= top) return null;
  return { x: left, y: top, w: right - left, h: bottom - top };
}

/**
 * Shifts a colour toward black or white by `shade`.
 *
 * Kept pure and separate so the palette maths can be checked: a shade of 0 must
 * return the colour untouched, or every texture would be subtly wrong.
 */
export function shadeColour(
  base: readonly [number, number, number],
  shade: number,
): [number, number, number] {
  const t = Math.max(-1, Math.min(1, shade));
  const target = t >= 0 ? 1 : 0;
  const amount = Math.abs(t);
  return [
    base[0] + (target - base[0]) * amount,
    base[1] + (target - base[1]) * amount,
    base[2] + (target - base[2]) * amount,
  ];
}

/** `rgb()` for a 0..1 triple. */
export function cssColour(c: readonly [number, number, number]): string {
  const byte = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return `rgb(${byte(c[0])}, ${byte(c[1])}, ${byte(c[2])})`;
}

/**
 * Paints a pattern onto a 2D context.
 *
 * The only function here that touches a canvas, and deliberately thin: it fills a
 * background, then one rectangle per patch. Everything that could be wrong about
 * *where* those rectangles go is in `patches`, which is pure and tested.
 */
export function paint(
  ctx: CanvasRenderingContext2D,
  pattern: Pattern,
  base: readonly [number, number, number],
  size = TEXTURE_SIZE,
  salt = 0,
): void {
  // The mortar shows through the gaps, so the background is the darkest part.
  ctx.fillStyle = cssColour(shadeColour(base, -0.55));
  ctx.fillRect(0, 0, size, size);

  for (const patch of patches(pattern, size, salt)) {
    ctx.fillStyle = cssColour(shadeColour(base, patch.shade));
    ctx.fillRect(patch.x, patch.y, patch.w, patch.h);
  }

  // A wash of fine grain over everything, so no surface is perfectly flat under
  // the light. Cheap: one pass of small rectangles at low alpha.
  if (pattern.grain > 0) {
    const step = 4;
    for (let y = 0; y < size; y += step) {
      for (let x = 0; x < size; x += step) {
        const n = hash(x, y, salt + 101);
        ctx.fillStyle = `rgba(0, 0, 0, ${(n * pattern.grain * 0.35).toFixed(3)})`;
        ctx.fillRect(x, y, step, step);
      }
    }
  }
}
